import { useDispatch, useSelector } from "react-redux";
import CustomModel from "../../../../components/custom-model/CustomModel";
import { selectApheresis } from "../../../slices/qualityApproval";
import { notify } from "../../../utils/methods/notification";
import * as Yup from "yup";
import { selectCurrentCycle } from "../../../slices/common";

const PublishProductModel = ({ publishModalOpen, setPublishModalOpen, data, getTimeLine }) => {
    const dispatch = useDispatch();
    const currentCycle = useSelector(selectCurrentCycle);
    
    const cr_fields = currentCycle?.data?.find((it) => it?.slug === 'qc-testing')?.change_requests ?? [];
    const is_cr = cr_fields.length > 0;

    // Check which fields have active change requests
    const hasDateCR = cr_fields.some((cr) =>
        cr.fields?.some((f) => f.slug === "change-publish-product-date")
    );
    const hasCommentCR = cr_fields.some((cr) =>
        cr.fields?.some((f) => f.slug === "change-publish-product-comment")
    );

    // If no CR exists, both fields are enabled. 
    // If CR exists, only fields with a matching CR slug are enabled.
    const isDateDisabled   = is_cr ? !hasDateCR    : false;
    const isCommentDisabled = is_cr ? !hasCommentCR : false;

    const modelData = {
        title: "Publish Product",
        children: [
            {
                type: 'date',
                name: 'manufacturedDate',
                label: "QC Publish date",
                minDate: false,
                disabled: isDateDisabled,
            },
            {
                type: 'description',
                name: 'comment',
                label: "Comments",
                placeholder: 'Enter Comment',
                IsRequired: true,
                disabled: isCommentDisabled,
            }
        ]
    };

    const qcTestingStep = currentCycle?.data?.find((it) => it?.slug === 'qc-testing');
    
    // Check if the step has been completed in this cycle
    const isCompleted = qcTestingStep?.product_completed_date != null && qcTestingStep?.product_completed_date !== 'null';
    
    // Check if the fields should be populated (if completed or if there's a Change Request active)
    const isPublished = isCompleted || is_cr;

    const savedDate = data?.patient_information?.completed_date || qcTestingStep?.product_completed_date;
    const savedComment = data?.patient_information?.product_comment;

    const modalInitialValues = {
        comment: isPublished ? (savedComment ?? "") : "",
        manufacturedDate: isPublished ? (savedDate ?? new Date()) : new Date()
    };

    const validationSchema = Yup.object().shape({
        comment: Yup.string()
            .required("Comment is required")
            .min(2, "Comment must be at least 2 characters")
            .max(500, "Comment must be at most 500 characters"),
    });

    const handlePublish = async (values) => {
        if (!data) return;
        try {
            const payload = {
                product_id: data?.product_id,
                is_cr,
                patient_id: data?.patient_information?.id,
                completed_date: values.manufacturedDate,
                feasibility_assessment_id: currentCycle?.feasibility_assessments_id,
                product_manufacturing_id: currentCycle?.product_manufacturing_id,
                qr_code_generated_id: data?.qrCode_id,
                comment: values.comment,
            };
            await dispatch(selectApheresis(payload)).unwrap();
            notify.success("Product published successfully!");
            await getTimeLine();
            setPublishModalOpen(false);
        } catch (error) {
            console.error("Error selecting apheresis:", error);
            const errorMessage =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to publish product. Please try again.";
            notify.error(errorMessage);
            setPublishModalOpen(false);
        }
    };

    return (
        <CustomModel
            key={`${data?.patient_information?.id || 'new'}_${publishModalOpen}`}
            modelData={modelData}
            open={publishModalOpen}
            setOpen={setPublishModalOpen}
            modalInitialValues={modalInitialValues}
            initialValuesValidationSchema={validationSchema}
            handleModelValues={handlePublish}
        />
    );
};

export default PublishProductModel;